import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography, ToggleButtonGroup, Button, IconButton } from '@mui/joy';
import {
  DeviceMobile as MobileIcon,
  DeviceTablet as TabletIcon,
  Desktop as DesktopIcon,
  ArrowsClockwise as RefreshIcon,
  ArrowsOut as FullscreenIcon,
  ArrowSquareOut as OpenNewIcon,
} from '@phosphor-icons/react';
import { VisualEditorProvider, useVisualEditor } from '../../contexts/VisualEditorContext';
import { useIframePostMessage } from '../../hooks/useIframePostMessage';
import api from '../../lib/api';
import { EditorOverlayContainer } from './EditorOverlayContainer';
import { PropertyPanel } from './PropertyPanel';
import type {
  VisualEditorProps,
  SectionClickedPayload,
  SectionHoveredPayload,
  FieldClickedPayload,
  TextEditedPayload,
  TextEditCompletePayload,
  SectionBoundsResponsePayload,
  ScrollPositionPayload,
} from '../../types/visualEditor';

type DeviceMode = 'mobile' | 'tablet' | 'desktop';

const DEVICE_SIZES = {
  mobile: { width: 375, height: 667, label: 'iPhone SE' },
  tablet: { width: 768, height: 1024, label: 'iPad' },
  desktop: { width: 1366, height: 768, label: 'Desktop' },
};

interface VisualEditorInnerProps extends VisualEditorProps {
  onEditSection: (index: number) => void;
}

const VisualEditorInner: React.FC<VisualEditorInnerProps> = ({
  siteId,
  pageData,
  siteData,
  manifest,
  onSectionsChange,
  onSectionSelect,
  onEditSection,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayContainerRef = useRef<HTMLDivElement>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeScrollTop, setIframeScrollTop] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const {
    selectedSectionIndex,
    hoveredSectionIndex,
    isDragging,
    dragSourceIndex,
    dragOverIndex,
    sectionBounds,
    selectSection,
    hoverSection,
    startInlineEdit,
    stopInlineEdit,
    updateFieldValue,
    startDrag,
    updateDragOver,
    endDrag,
    moveSection,
    deleteSection,
    duplicateSection,
    openAddSectionModal,
    setIframeReady,
    updateSectionBounds,
  } = useVisualEditor();

  // Fetch signed preview URL from backend
  useEffect(() => {
    const fetchPreviewUrl = async () => {
      try {
        const slug = pageData.slug === 'index' ? 'index' : pageData.slug;
        const response = await api.get<{ preview_url: string }>(`/api/cms/sites/${siteId}/generate-preview-url/?slug=${encodeURIComponent(slug)}`);
        setPreviewUrl(response.data.preview_url);
      } catch (error) {
        console.error('Failed to generate preview URL:', error);
        // Set a fallback URL that will show an error
        const slug = pageData.slug === 'index' ? '' : pageData.slug;
        setPreviewUrl(`/cms/sites/preview/${slug}`);
      }
    };
    fetchPreviewUrl();
  }, [siteId, pageData.slug]);

  // PostMessage communication
  const {
    isReady: iframeIsReady,
    selectSection: iframeSelectSection,
    deselectAll: iframeDeselectAll,
    highlightSection: iframeHighlightSection,
    unhighlightSection: iframeUnhighlightSection,
    enableInlineEdit: iframeEnableInlineEdit,
    disableInlineEdit: iframeDisableInlineEdit,
    updateFieldValue: iframeUpdateFieldValue,
    scrollToSection: iframeScrollToSection,
    requestSectionBounds,
    refreshPreview,
  } = useIframePostMessage({
    iframeRef,
    onEditorReady: () => {
      setIframeReady(true);
      // Request initial bounds
      requestSectionBounds().catch(console.error);
    },
    onSectionClicked: (payload: SectionClickedPayload) => {
      selectSection(payload.sectionIndex);
      onSectionSelect?.(payload.sectionIndex);
    },
    onSectionHovered: (payload: SectionHoveredPayload) => {
      if (!isDragging) {
        hoverSection(payload.sectionIndex);
      }
    },
    onSectionUnhovered: () => {
      if (!isDragging) {
        hoverSection(null);
      }
    },
    onFieldClicked: (payload: FieldClickedPayload) => {
      selectSection(payload.sectionIndex);
      startInlineEdit(payload.sectionIndex, payload.fieldName);
      // Enable contenteditable in iframe
      iframeEnableInlineEdit(payload.sectionIndex, payload.fieldName);
    },
    onTextEdited: (payload: TextEditedPayload) => {
      updateFieldValue(payload.sectionIndex, payload.fieldName, payload.newValue);
    },
    onTextEditComplete: (payload: TextEditCompletePayload) => {
      updateFieldValue(payload.sectionIndex, payload.fieldName, payload.finalValue);
      stopInlineEdit();
    },
    onSectionBounds: (payload: SectionBoundsResponsePayload) => {
      console.log('[VisualEditor] Received section bounds:', payload.sections.length, 'sections');
      updateSectionBounds(payload);
    },
    onScrollPosition: (payload: ScrollPositionPayload) => {
      setIframeScrollTop(payload.scrollTop);
    },
  });

  // Sync selection to iframe
  useEffect(() => {
    if (!iframeIsReady) return;

    if (selectedSectionIndex !== null) {
      iframeSelectSection(selectedSectionIndex);
    } else {
      iframeDeselectAll();
    }
  }, [selectedSectionIndex, iframeIsReady, iframeSelectSection, iframeDeselectAll]);

  // Sync hover to iframe
  useEffect(() => {
    if (!iframeIsReady || isDragging) return;

    if (hoveredSectionIndex !== null && hoveredSectionIndex !== selectedSectionIndex) {
      iframeHighlightSection(hoveredSectionIndex);
    }
  }, [hoveredSectionIndex, selectedSectionIndex, iframeIsReady, isDragging, iframeHighlightSection]);

  // Calculate scale to fit preview in container
  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth - 40;
      const containerHeight = container.clientHeight - 100;

      const deviceSize = DEVICE_SIZES[deviceMode];
      const scaleX = containerWidth / deviceSize.width;
      const scaleY = containerHeight / deviceSize.height;

      setScale(Math.min(scaleX, scaleY, 1));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [deviceMode, isFullscreen]);

  // Request bounds on resize/device change
  useEffect(() => {
    if (iframeIsReady) {
      const timeoutId = setTimeout(() => {
        requestSectionBounds().catch(console.error);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [deviceMode, scale, iframeIsReady, requestSectionBounds]);

  // Cleanup refresh timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const handleRefresh = useCallback(() => {
    if (iframeRef.current) {
      setIsLoading(true);
      iframeRef.current.src = iframeRef.current.src;
    }
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const handleOpenInNew = useCallback(() => {
    window.open(previewUrl, '_blank');
  }, [previewUrl]);

  const handleSectionReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      moveSection(fromIndex, toIndex);
    },
    [moveSection]
  );

  // Handle field change in PropertyPanel
  const handleFieldChange = useCallback(
    (fieldName: string, value: unknown) => {
      if (selectedSectionIndex === null) return;

      const newSections = [...pageData.sections_draft];
      if (newSections[selectedSectionIndex]) {
        newSections[selectedSectionIndex] = {
          ...newSections[selectedSectionIndex],
          data: {
            ...newSections[selectedSectionIndex].data,
            [fieldName]: value,
          },
        };
        onSectionsChange(newSections);

        // Try immediate update for text fields (works if template has data-field-name)
        iframeUpdateFieldValue(selectedSectionIndex, fieldName, String(value));

        // Debounced refresh - wait 800ms after last change for full re-render
        // This ensures auto-save has time to complete before refresh
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(() => {
          refreshPreview();
        }, 800);
      }
    },
    [selectedSectionIndex, pageData.sections_draft, onSectionsChange, iframeUpdateFieldValue, refreshPreview]
  );

  // Handle variant change in PropertyPanel
  const handleVariantChange = useCallback(
    (variant: string) => {
      if (selectedSectionIndex === null) return;

      const newSections = [...pageData.sections_draft];
      if (newSections[selectedSectionIndex]) {
        newSections[selectedSectionIndex] = {
          ...newSections[selectedSectionIndex],
          variant,
        };
        onSectionsChange(newSections);

        // Variant changes require full re-render - debounced refresh
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(() => {
          refreshPreview();
        }, 800);
      }
    },
    [selectedSectionIndex, pageData.sections_draft, onSectionsChange, refreshPreview]
  );

  // Get current selected section data
  const selectedSection = selectedSectionIndex !== null
    ? pageData.sections_draft[selectedSectionIndex]
    : null;
  const selectedSectionConfig = selectedSection
    ? manifest[selectedSection.type]
    : null;

  const deviceSize = DEVICE_SIZES[deviceMode];

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.level1',
        borderRadius: 'md',
        overflow: 'hidden',
      }}
    >
      {/* Preview Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.surface',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography level="title-md">Visual Editor</Typography>
          {deviceMode !== 'desktop' && (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {deviceSize.label} ({deviceSize.width} × {deviceSize.height})
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={deviceMode}
            onChange={(event, value) => value && setDeviceMode(value)}
            size="sm"
          >
            <Button value="mobile" startDecorator={<MobileIcon />}>
              Mobile
            </Button>
            <Button value="tablet" startDecorator={<TabletIcon />}>
              Tablet
            </Button>
            <Button value="desktop" startDecorator={<DesktopIcon />}>
              Desktop
            </Button>
          </ToggleButtonGroup>

          <IconButton size="sm" variant="outlined" onClick={handleRefresh} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>

          <IconButton size="sm" variant="outlined" onClick={handleFullscreen}>
            <FullscreenIcon />
          </IconButton>

          <IconButton size="sm" variant="outlined" onClick={handleOpenInNew}>
            <OpenNewIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Content Area - Preview + Property Panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Preview Container */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              bgcolor: 'background.surface',
              borderRadius: 'md',
              p: 3,
              boxShadow: 'lg',
            }}
          >
            <CircularProgress />
            <Typography level="body-sm" sx={{ mt: 2 }}>
              Loading preview...
            </Typography>
          </Box>
        )}

        {/* Device Frame with Overlay Container */}
        <Box
          sx={{
            width: deviceSize.width,
            height: deviceSize.height,
            transform: `scale(${scale})`,
            transformOrigin: 'center',
            transition: 'all 0.3s ease',
            bgcolor: 'background.surface',
            borderRadius: deviceMode === 'mobile' ? '2rem' : '0.5rem',
            boxShadow: 'xl',
            border: deviceMode !== 'desktop' ? '8px solid' : 'none',
            borderColor: 'neutral.800',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Mobile Notch */}
          {deviceMode === 'mobile' && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 150,
                height: 25,
                bgcolor: 'neutral.800',
                borderRadius: '0 0 1rem 1rem',
                zIndex: 2,
              }}
            />
          )}

          {/* Iframe */}
          <iframe
            ref={iframeRef}
            src={previewUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: 'white',
            }}
            onLoad={() => {
              setIsLoading(false);
            }}
            title="Visual Page Editor"
            sandbox="allow-scripts allow-same-origin allow-forms"
          />

          {/* Overlay Container for editing UI */}
          {iframeIsReady && (
            <EditorOverlayContainer
              ref={overlayContainerRef}
              iframeRef={iframeRef}
              sections={pageData.sections_draft}
              manifest={manifest}
              sectionBounds={sectionBounds}
              selectedIndex={selectedSectionIndex}
              hoveredIndex={hoveredSectionIndex}
              isDragging={isDragging}
              dragSourceIndex={dragSourceIndex}
              dragOverIndex={dragOverIndex}
              iframeScrollTop={iframeScrollTop}
              iframeScale={scale}
              onSectionSelect={selectSection}
              onSectionHover={hoverSection}
              onSectionReorder={handleSectionReorder}
              onAddSection={openAddSectionModal}
              onDeleteSection={deleteSection}
              onDuplicateSection={duplicateSection}
              onEditSection={onEditSection}
              onStartDrag={startDrag}
              onDragOver={updateDragOver}
              onEndDrag={endDrag}
            />
          )}
        </Box>
        </Box>

        {/* Property Panel Sidebar */}
        <PropertyPanel
          section={selectedSection}
          sectionConfig={selectedSectionConfig}
          sectionIndex={selectedSectionIndex}
          onChange={handleFieldChange}
          onVariantChange={handleVariantChange}
          onClose={() => selectSection(null)}
          siteId={siteId}
        />
      </Box>
    </Box>
  );
};

// Main exported component with Provider
export const VisualEditor: React.FC<VisualEditorProps & { onEditSection: (index: number) => void }> = (props) => {
  const handleSectionBoundsRequest = useCallback(() => {
    // This is handled internally by the VisualEditorInner via useIframePostMessage
  }, []);

  return (
    <VisualEditorProvider
      sections={props.pageData.sections_draft}
      onSectionsChange={props.onSectionsChange}
      onSectionBoundsRequest={handleSectionBoundsRequest}
    >
      <VisualEditorInner {...props} />
    </VisualEditorProvider>
  );
};
